import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import StudentIdCard from '@/components/center/StudentIdCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dedicated print page for Student ID Card.
 * - No sidebar, no header, no modal chrome.
 * - Uses the exact same StudentIdCard component as the in-app preview.
 * - Fixed CR80 dimensions (mm) so preview == print == final output.
 * - Pulls live joined data (student + course + center + computed end date)
 *   from /api/center/students/{id}/id-card so the print is always in sync
 *   with the latest profile, course rename, center logo, etc.
 */
const PrintStudentIdCard = () => {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('center_token');
        const res = await axios.get(`${API}/center/students/${studentId}/id-card`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to load student');
      }
    };
    load();
  }, [studentId]);

  // Auto-print once the card + images are fully loaded.
  useEffect(() => {
    if (!data) return;
    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      setTimeout(() => window.print(), 300);
    };
    const images = Array.from(document.querySelectorAll('.iqra-student-card img'));
    if (images.length === 0) {
      triggerPrint();
      return;
    }
    let pending = images.length;
    const done = () => {
      pending -= 1;
      if (pending <= 0) triggerPrint();
    };
    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        done();
      } else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
    const fallback = setTimeout(triggerPrint, 3000);
    return () => clearTimeout(fallback);
  }, [data]);

  if (error) {
    return (
      <div style={{ padding: '20mm', fontFamily: 'Arial, sans-serif', color: '#b91c1c' }}>
        Error: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: '20mm', fontFamily: 'Arial, sans-serif', color: '#475569' }}>
        Loading ID card…
      </div>
    );
  }

  return (
    <>
      <style>{`
        @page { size: 54mm 85.6mm; margin: 0; }
        html, body, #root {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .print-stage {
          width: 54mm;
          height: 85.6mm;
          margin: 0 auto;
          display: block;
        }
        .iqra-student-card { box-shadow: none !important; }
        .screen-controls { position: fixed; top: 16px; right: 16px; z-index: 9999; display: flex; gap: 8px; }
        .screen-controls button {
          background: #0f7a75; color: #fff; border: 0; padding: 8px 14px;
          border-radius: 6px; font-family: inherit; font-size: 13px; cursor: pointer;
        }
        @media screen {
          body { background: #e5e7eb !important; padding: 24px 0; }
          .print-stage { box-shadow: 0 10px 30px rgba(0,0,0,0.18); border-radius: 3mm; }
        }
        @media print {
          .screen-controls { display: none !important; }
        }
      `}</style>

      <div className="screen-controls no-print">
        <button onClick={() => window.print()} data-testid="student-print-btn">Print</button>
        <button onClick={() => window.close()} data-testid="student-close-btn">Close</button>
      </div>

      <div className="print-stage">
        <StudentIdCard data={data} scale={1} />
      </div>
    </>
  );
};

export default PrintStudentIdCard;
